import { AcademyGuideSummaryText } from "@faire/deprecated-retailer-visitor-shared/views/open-with-faire/academy/components/GuideCards/AcademyGuideSummaryText";
import {
  AnimatedUnderlineText,
  UnderlineHoverStyle,
  ZoomHoverStyle,
  ZoomImage,
  ZoomImageContainer,
} from "@faire/deprecated-retailer-visitor-shared/views/open-with-faire/academy/components/GuideCards/HoverStyles";
import { GUIDE_CARDS } from "@faire/deprecated-retailer-visitor-shared/views/open-with-faire/academy/guidePages/const";
import type { FaireAcademyGuide } from "@faire/deprecated-retailer-visitor-shared/views/open-with-faire/academy/types";
import { IImage } from "@faire/web-api--source/indigofair/data/IImage";
import { optimizeImageUrlFillRectangle } from "@faire/web/common/images/optimizeImages";
import { ITraceEventMarker } from "@faire/web/common/pete/TraceEventMarkerTracker";
import { useIsSSR } from "@faire/web/common/server-side-rendering/isSSR";
import { Column } from "@faire/web/slate/Layout";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { Typography } from "@faire/web/slate/Typography";
import { Link } from "@faire/web/ui/LinkAnchor";
import * as React from "react";
import styled from "styled-components";

const SMALL_CARD_IMG_HEIGHT = 340;
const SMALL_CARD_IMG_SMALL_HEIGHT = 250;
const SMALL_CARD_IMG_WIDTH = 342;
const BACKGROUND_IMG_HEIGHT = 150;
const BACKGROUND_IMAGE =
  "https://cdn.faire.com/fastly/79d3fe30a1680caa8ad67519c5ef6263ad26e7a47c0bdc7bf63af82f9ad0825e.jpeg";

export const AcademyGuideCardSmall: React.FC<{
  guide: FaireAcademyGuide;
  fullWidth?: boolean;
  useSmallHeight?: boolean;
  onClick?: () => ITraceEventMarker;
  className?: string;
}> = ({
  guide,
  fullWidth = false,
  useSmallHeight = false,
  onClick,
  className,
}) => {
  const isSSR = useIsSSR();
  const { title, featuredImage, readTimeMinutes, chapters, linkToPage, likes } =
    guide;
  const guideIndex = GUIDE_CARDS.findIndex((guide) => guide.title === title);
  const backgroundPositionX = Math.floor(
    (guideIndex / GUIDE_CARDS.length) * 100
  );
  const backgroundImageUrl = optimizeImageUrlFillRectangle(
    IImage.build({ url: BACKGROUND_IMAGE }),
    isSSR,
    BACKGROUND_IMG_HEIGHT * 2.66,
    BACKGROUND_IMG_HEIGHT
  );
  const smallCardImgHeight = useSmallHeight
    ? SMALL_CARD_IMG_SMALL_HEIGHT
    : SMALL_CARD_IMG_HEIGHT;

  return (
    <Link to={linkToPage} onClick={onClick} className={className}>
      <ColumnContainer $fullWidth={fullWidth}>
        <ZoomImageContainer
          $height={smallCardImgHeight}
          $width={SMALL_CARD_IMG_WIDTH}
          $fullWidth={fullWidth}
        >
          <ZoomImage
            src={optimizeImageUrlFillRectangle(
              IImage.build({ url: featuredImage }),
              isSSR,
              SMALL_CARD_IMG_WIDTH * 2,
              smallCardImgHeight * 2
            )}
            height={smallCardImgHeight}
            width={fullWidth ? "100%" : SMALL_CARD_IMG_WIDTH}
            alt=""
          />
        </ZoomImageContainer>
        <CopyContainer
          justify="center"
          $backgroundImage={backgroundImageUrl}
          $backgroundPositionX={backgroundPositionX}
          $totalNumGuides={GUIDE_CARDS.length}
        >
          <Typography variant="pageHeaderSerifRegular">
            <AnimatedUnderlineText>{title}</AnimatedUnderlineText>
          </Typography>
          <Spacer height="1x" />
          <AcademyGuideSummaryText
            numChapters={chapters.length}
            readTimeMinutes={readTimeMinutes}
            likes={likes}
          />
        </CopyContainer>
      </ColumnContainer>
    </Link>
  );
};

const ColumnContainer = styled(Column)<{
  $fullWidth: boolean;
}>`
  width: ${({ $fullWidth }) => ($fullWidth ? "100%" : "342px")};
  height: 100%;

  &:hover {
    cursor: pointer;

    ${UnderlineHoverStyle}

    ${ZoomHoverStyle}
  }
`;

const CopyContainer = styled(Column)<{
  $backgroundImage: string | undefined;
  $backgroundPositionX: number;
  $totalNumGuides: number;
}>`
  background-image: url("${({ $backgroundImage }) => $backgroundImage}");
  background-position: ${({ $backgroundPositionX }) => $backgroundPositionX}% 0%;
  background-size: ${({ $totalNumGuides }) => $totalNumGuides * 100}% 100%;
  padding: ${getSpacing("3x")};
  flex: 1;
`;
